import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmModal from '../../common/ConfirmModal';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../owners/formatMoney';
import { FaTimes, FaCloudUploadAlt, FaCheckCircle, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';

function formatDateTime(ts) {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function Badge({ children, tone = 'gray' }) {
    const classes = {
        gray: 'bg-gray-100 text-gray-700',
        green: 'bg-green-100 text-green-700',
        amber: 'bg-amber-100 text-amber-700',
        red: 'bg-red-100 text-red-700',
        blue: 'bg-blue-100 text-blue-700',
    };
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${classes[tone]}`}>{children}</span>;
}

const C2BRecoveryImportModal = ({ isOpen, onClose, onImported }) => {
    const { token } = useAuth();
    const [file, setFile] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [rows, setRows] = useState([]);
    const [selectedReceiptNos, setSelectedReceiptNos] = useState(new Set());
    const [previewLoading, setPreviewLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [error, setError] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setFile(null);
            setMetadata(null);
            setRows([]);
            setSelectedReceiptNos(new Set());
            setPreviewLoading(false);
            setImportLoading(false);
            setError('');
            setShowConfirm(false);
        }
    }, [isOpen]);

    const authToken = token || localStorage.getItem('authToken');
    const importableRows = useMemo(() => rows.filter((row) => row.importable && !row.already_imported), [rows]);
    const selectedRows = useMemo(
        () => rows.filter((row) => selectedReceiptNos.has(row.receipt_no)),
        [rows, selectedReceiptNos]
    );
    const selectedCount = selectedRows.length;

    const toggleReceipt = (receiptNo) => {
        setSelectedReceiptNos((prev) => {
            const next = new Set(prev);
            if (next.has(receiptNo)) {
                next.delete(receiptNo);
            } else {
                next.add(receiptNo);
            }
            return next;
        });
    };

    const toggleAllImportable = () => {
        setSelectedReceiptNos((prev) => {
            const next = new Set(prev);
            const allSelected = importableRows.every((row) => next.has(row.receipt_no));
            if (allSelected) {
                importableRows.forEach((row) => next.delete(row.receipt_no));
            } else {
                importableRows.forEach((row) => next.add(row.receipt_no));
            }
            return next;
        });
    };

    const handlePreview = async () => {
        if (!file) {
            setError('Choose a CSV file first.');
            return;
        }

        setPreviewLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${rootUrl}/api/payments/c2b/recovery/preview`, formData, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });
            const previewMetadata = res.data?.data?.metadata || null;
            const previewRows = res.data?.data?.rows || [];
            setMetadata(previewMetadata);
            setRows(previewRows);
            setSelectedReceiptNos(new Set(previewRows.filter((row) => row.importable && !row.already_imported).map((row) => row.receipt_no)));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to preview the CSV file.');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleImport = async () => {
        setImportLoading(true);
        setError('');
        try {
            const res = await axios.post(`${rootUrl}/api/payments/c2b/recovery/import`, {
                metadata,
                rows: selectedRows,
            }, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });

            if (!res.data?.success) {
                throw new Error(res.data?.message || 'Import failed');
            }

            onImported?.(res.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to import selected rows.');
        } finally {
            setImportLoading(false);
            setShowConfirm(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black bg-opacity-30" onClick={onClose} />
            <div className="fixed inset-y-0 right-0 z-50 w-full max-w-5xl bg-[#faf8f6] shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white">
                    <div>
                        <div className="flex items-center gap-2">
                            <FaCloudUploadAlt className="text-[#FF5733]" />
                            <h2 className="text-base font-semibold text-gray-900">Recover C2B Transactions from CSV</h2>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Upload the statement export, preview the transaction rows, and import only the missing receipts.</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100" aria-label="Close">
                        <FaTimes />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-5">
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                        <div className="flex flex-col md:flex-row md:items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-600 mb-1">CSV file</label>
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-[#FF5733] hover:file:bg-orange-100"
                                />
                                {file && <p className="text-xs text-gray-500 mt-1">{file.name}</p>}
                            </div>
                            <button
                                type="button"
                                onClick={handlePreview}
                                disabled={previewLoading || !file}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {previewLoading ? <FaSpinner className="animate-spin" /> : <FaCloudUploadAlt />}
                                Preview CSV
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">{error}</div>
                    )}

                    {metadata && (
                        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                                <h3 className="text-sm font-semibold text-gray-900">Report Summary</h3>
                                <Badge tone="blue">{rows.length} transaction rows</Badge>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div><span className="text-gray-500">Account Holder:</span> <span className="font-medium text-gray-800">{metadata.account_holder || '—'}</span></div>
                                <div><span className="text-gray-500">Short Code:</span> <span className="font-medium text-gray-800">{metadata.short_code || '—'}</span></div>
                                <div><span className="text-gray-500">Account:</span> <span className="font-medium text-gray-800">{metadata.account || '—'}</span></div>
                                <div><span className="text-gray-500">Operator:</span> <span className="font-medium text-gray-800">{metadata.operator || '—'}</span></div>
                                <div><span className="text-gray-500">Time Period:</span> <span className="font-medium text-gray-800">{metadata.time_period_from || '—'} → {metadata.time_period_to || '—'}</span></div>
                                <div><span className="text-gray-500">Report Date:</span> <span className="font-medium text-gray-800">{metadata.date_of_report || '—'}</span></div>
                            </div>
                        </div>
                    )}

                    {rows.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-gray-900">Transaction Rows</h3>
                                    <Badge tone="green">{selectedCount} selected</Badge>
                                </div>
                                <button
                                    type="button"
                                    onClick={toggleAllImportable}
                                    className="text-sm font-medium text-[#FF5733] hover:text-[#e64520]"
                                >
                                    {importableRows.every((row) => selectedReceiptNos.has(row.receipt_no)) ? 'Clear importable' : 'Select importable'}
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-2 text-left w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={importableRows.length > 0 && importableRows.every((row) => selectedReceiptNos.has(row.receipt_no))}
                                                    onChange={toggleAllImportable}
                                                    className="rounded border-gray-300 text-[#FF5733] focus:ring-[#FF5733]"
                                                />
                                            </th>
                                            <th className="px-4 py-2 text-left font-semibold text-gray-700">Receipt</th>
                                            <th className="px-4 py-2 text-left font-semibold text-gray-700">Completion</th>
                                            <th className="px-4 py-2 text-right font-semibold text-gray-700">Amount</th>
                                            <th className="px-4 py-2 text-left font-semibold text-gray-700">Ref</th>
                                            <th className="px-4 py-2 text-left font-semibold text-gray-700">Plate</th>
                                            <th className="px-4 py-2 text-left font-semibold text-gray-700">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => {
                                            const checked = selectedReceiptNos.has(row.receipt_no);
                                            return (
                                                <tr key={row.receipt_no} className="border-b border-gray-100 hover:bg-orange-50/60">
                                                    <td className="px-4 py-2 align-top">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            disabled={!row.importable || row.already_imported}
                                                            onChange={() => toggleReceipt(row.receipt_no)}
                                                            className="rounded border-gray-300 text-[#FF5733] focus:ring-[#FF5733] disabled:opacity-40"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2 align-top">
                                                        <div className="font-mono text-xs font-medium text-gray-800">{row.receipt_no || '—'}</div>
                                                        <div className="text-xs text-gray-500 mt-1">{row.other_party_info || row.details || '—'}</div>
                                                    </td>
                                                    <td className="px-4 py-2 align-top text-xs text-gray-700 whitespace-nowrap">{formatDateTime(row.trans_time)}</td>
                                                    <td className="px-4 py-2 align-top text-right font-semibold text-gray-900 whitespace-nowrap">{formatMoney(row.paid_in || 0)}</td>
                                                    <td className="px-4 py-2 align-top font-mono text-xs text-gray-700">{row.bill_ref_raw || '—'}</td>
                                                    <td className="px-4 py-2 align-top font-mono text-xs text-gray-800">{row.plate_no || '—'}</td>
                                                    <td className="px-4 py-2 align-top">
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {row.already_imported ? (
                                                                <Badge tone="gray"><FaCheckCircle /> Imported</Badge>
                                                            ) : row.importable ? (
                                                                <Badge tone="green"><FaCheckCircle /> Missing</Badge>
                                                            ) : (
                                                                <Badge tone="amber"><FaExclamationTriangle /> {row.reason || 'Skipped'}</Badge>
                                                            )}
                                                            <Badge tone={row.match_status === 'matched' ? 'blue' : 'amber'}>
                                                                {row.match_status === 'matched' ? 'Matched' : 'Unmatched'}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-gray-200 bg-white flex items-center justify-between gap-3">
                    <div className="text-xs text-gray-500">
                        {selectedCount > 0
                            ? `${selectedCount} row${selectedCount === 1 ? '' : 's'} ready to import`
                            : 'Upload a CSV to preview rows before importing.'}
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowConfirm(true)}
                        disabled={selectedCount === 0 || importLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {importLoading ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                        Import selected rows
                    </button>
                </div>
            </div>

            <ConfirmModal
                isOpen={showConfirm}
                title="Import selected C2B rows?"
                description={`This will seed ${selectedCount} selected row${selectedCount === 1 ? '' : 's'} into the inbound table and keep the current C2B posting flow intact.`}
                onCancel={() => setShowConfirm(false)}
                onConfirm={handleImport}
                confirmText="Import"
                cancelText="Cancel"
                loading={importLoading}
                error={error}
                variant="primary"
            />
        </>
    );
};

export default C2BRecoveryImportModal;
